// The table's own session (docs/VTT/MVP-BUILD-PLAN.md §2.2).
//
// Tokens live in THIS origin's localStorage, never the site's, and every call
// goes to this origin: the VTT server trades the site's handoff code for a
// pair of the table's own and proxies refresh. The claims on the access token
// give a provisional name at first paint; /api/me, read live from the site's
// table, replaces it.
import { io, type Socket } from 'socket.io-client';
import { TABLE_TOKEN } from '@dragons-table/vtt-shared';
import { tellLocked } from './lock';

const ACCESS_KEY = 'vtt.accessToken';
const REFRESH_KEY = 'vtt.refreshToken';

export interface SessionUser {
  id: string;
  handle: string | null;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  user: SessionUser;
}

const read = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const write = (key: string, value: string | null) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch { /* storage unavailable: the in-memory copy still serves this page */ }
};

let accessToken = read(ACCESS_KEY);
let refreshToken = read(REFRESH_KEY);

export const getAccessToken = () => accessToken;
export const hasSession = () => accessToken !== null;

export function setTokens(pair: { access_token: string; refresh_token: string } | null): void {
  accessToken = pair?.access_token ?? null;
  refreshToken = pair?.refresh_token ?? null;
  write(ACCESS_KEY, accessToken);
  write(REFRESH_KEY, refreshToken);
}

/** The site's origin, for the way back when there is no session. */
export const siteUrl = (): string => (import.meta.env.VITE_SITE_URL || '').replace(/\/+$/, '');

/** Where a person with no session is sent: the site's own door to this table. */
export const siteTableUrl = (campaignId: string): string => `${siteUrl()}/table/${encodeURIComponent(campaignId)}`;

/** The handoff code in this page's fragment, and the fragment stripped so the code is never kept in history. */
export function takeHandoffCode(loc: Location = window.location, hist: History = window.history): string | null {
  const m = /(?:^#|&)code=([A-Za-z0-9_-]+)/.exec(loc.hash);
  if (!m) return null;
  hist.replaceState(null, '', loc.pathname + loc.search);
  return m[1] ?? null;
}

function pairFrom(body: unknown): TokenPair | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  const user = b.user as Record<string, unknown> | undefined;
  if (typeof b.access_token !== 'string' || typeof b.refresh_token !== 'string' || !user || typeof user.id !== 'string') return null;
  return { access_token: b.access_token, refresh_token: b.refresh_token, user: { id: user.id, handle: typeof user.handle === 'string' ? user.handle : null } };
}

async function postJson(path: string, body: unknown, fetchImpl: typeof fetch): Promise<{ status: number; body: unknown }> {
  try {
    const res = await fetchImpl(path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    return { status: res.status, body: res.ok ? await res.json() : null };
  } catch {
    return { status: 0, body: null };
  }
}

/**
 * The tabletop's own server did not answer (no answer, a 5xx from it or the proxy, a 429). Not a refusal of the person:
 * sending them to the site's door would only bring them straight back to the same silence, over and over (a down
 * vtt-server bounced a browser between the two origins ~17 times a second, each bounce a handoff from the site).
 */
export const UNREACHABLE = 'unreachable' as const;
const unreachable = (status: number) => status === 0 || status === 429 || status >= 500;

/** Trade the site's handoff code for a session of this origin's own. */
export async function exchangeCode(code: string, fetchImpl: typeof fetch = fetch): Promise<SessionUser | null | typeof UNREACHABLE> {
  const answer = await postJson('/api/session/exchange', { code }, fetchImpl);
  if (unreachable(answer.status)) return UNREACHABLE;
  const pair = pairFrom(answer.body);
  if (!pair) return null;
  setTokens(pair);
  return pair.user;
}

// ── Keeping the session alive (issue 793) ─────────────────────────────────
// The access token lives fifteen minutes. Every call to this origin goes
// through `authFetch`, which sends the bearer and, on a 401, refreshes ONCE —
// one refresh shared by every caller waiting on it, since the refresh token
// rotates and a second refresh with the used one would be refused — and sends
// again. A refresh the server REFUSES ends the session, said once through
// `onSessionEnded`; a refresh that could not reach the server leaves the
// session as it is, so a network blip never signs anyone out.

type RefreshOutcome = 'ok' | 'refused' | 'offline';

let refreshing: Promise<RefreshOutcome> | null = null;
let ended = false;
const endedListeners = new Set<() => void>();
/** Told the new access token after every refresh that succeeds (the socket sends it on: `connectSocket`). */
const refreshedListeners = new Set<(accessToken: string) => void>();

/** True once a refresh was refused: the page cannot talk to the server again until it is reloaded. */
export const sessionEnded = (): boolean => ended;

/** Told once, when the session ends. Returns the unsubscribe. */
export function onSessionEnded(listener: () => void): () => void {
  endedListeners.add(listener);
  return () => { endedListeners.delete(listener); };
}

function endSession(): void {
  setTokens(null);
  if (ended) return;
  ended = true;
  for (const l of [...endedListeners]) l();
}

/** For tests: a fresh page's state. */
export function resetSessionForTests(): void {
  ended = false;
  refreshing = null;
  endedListeners.clear();
  refreshedListeners.clear();
}

function refreshOnce(fetchImpl: typeof fetch): Promise<RefreshOutcome> {
  if (refreshing) return refreshing;
  refreshing = (async (): Promise<RefreshOutcome> => {
    if (!refreshToken) { endSession(); return 'refused'; }
    const answer = await postJson('/api/session/refresh', { refresh_token: refreshToken }, fetchImpl);
    if (answer.status === 0 || answer.status === 429 || answer.status >= 500) return 'offline';
    const pair = pairFrom(answer.body);
    if (!pair) { endSession(); return 'refused'; }
    setTokens(pair);
    for (const l of [...refreshedListeners]) l(pair.access_token);
    return 'ok';
  })().finally(() => { refreshing = null; });
  return refreshing;
}

/** Rotate the pair through this origin. A refused refresh ends the session. */
export async function refreshSession(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  if (!refreshToken) return false;
  return (await refreshOnce(fetchImpl)) === 'ok';
}

/**
 * A request to this origin with the session's bearer. On a 401 the session is
 * refreshed once (shared with every other caller) and the request sent again;
 * the caller reads the answer it gets back as before. Throws only what fetch
 * throws.
 */
export async function authFetch(path: string, init: RequestInit = {}, fetchImpl: typeof fetch = fetch): Promise<Response> {
  const send = () => fetchImpl(path, {
    ...init,
    headers: { ...((init.headers ?? {}) as Record<string, string>), Authorization: `Bearer ${accessToken ?? ''}` },
  });
  const used = accessToken;
  const res = await send();
  if (res.status === 403) noteLocked(res);
  if (res.status !== 401 || ended) return res;
  // Another caller may have refreshed while this request was out: then only send again.
  if (accessToken === used) {
    const outcome = await refreshOnce(fetchImpl);
    if (outcome !== 'ok') return res;
  }
  if (!accessToken) return res;
  return send();
}

/** Early access (temporary): a 403 that says `locked` tells the page, read from a copy so the caller's answer is untouched. */
function noteLocked(res: Response): void {
  if (typeof res.clone !== 'function') return;
  void res.clone().json().then((b: { error?: unknown } | null) => { if (b?.error === 'locked') tellLocked(); }).catch(() => undefined);
}

/** The seconds-since-epoch expiry a JWT claims, or null when it does not read as one. */
function expiryOf(token: string): number | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

/** The access token to present now: refreshed first when it has expired or is about to. */
export async function freshAccessToken(fetchImpl: typeof fetch = fetch, now: () => number = Date.now): Promise<string | null> {
  const exp = accessToken ? expiryOf(accessToken) : null;
  if (accessToken && exp !== null && exp * 1000 - now() < 30_000) await refreshOnce(fetchImpl);
  return accessToken;
}

/** Who the session is, from the server; a stale access token is refreshed once. */
export async function fetchMe(fetchImpl: typeof fetch = fetch): Promise<SessionUser | null | typeof UNREACHABLE> {
  if (!accessToken) return null;
  let answer: { status: number; body: unknown };
  try {
    const res = await authFetch('/api/me', { headers: { Accept: 'application/json' } }, fetchImpl);
    answer = { status: res.status, body: res.ok ? await res.json() : null };
  } catch {
    answer = { status: 0, body: null };
  }
  if (answer.status !== 200) {
    if (answer.status === 401) setTokens(null);
    return unreachable(answer.status) ? UNREACHABLE : null;
  }
  const b = answer.body as Record<string, unknown> | null;
  return b && typeof b.id === 'string' ? { id: b.id, handle: typeof b.handle === 'string' ? b.handle : null } : null;
}

/**
 * A socket of this origin, authenticated with the session's bearer. It
 * reconnects on its own; every (re)connection joins the table afresh and is
 * answered with a fresh projected snapshot — never a replay (plan §2.5).
 *
 * The token is read on EVERY (re)connection, refreshed first when it has
 * expired (issue 793): fixed at the first connection, a reconnection after
 * fifteen minutes presented an expired token and the table stayed
 * disconnected until a reload. A handshake the server refuses is final for
 * socket.io, so a refusal of the token refreshes and connects again — once per
 * refusal, so a session that cannot be refreshed stops rather than loops.
 */
export function connectSocket(connect: typeof io = io, fetchImpl: typeof fetch = fetch): Socket {
  const socket = connect({
    transports: ['websocket'], reconnection: true, reconnectionDelayMax: 5000,
    auth: (cb: (data: object) => void) => { void freshAccessToken(fetchImpl).then((token) => cb({ token: token ?? '' })); },
  });
  // The handshake's token is fixed for the connection's life, and the server reads the site with it (a bug check of
  // issue 1306): a private system's rules, a homebrew target. So every refresh that succeeds — whoever caused it, the
  // one shared refresh of `authFetch` — sends the new token over the socket (`table:token`), and while connected the
  // token is refreshed shortly before it runs out, though nothing else calls the server. Never sent to anyone else.
  let timer: ReturnType<typeof setTimeout> | null = null;
  const keepFresh = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    const exp = accessToken ? expiryOf(accessToken) : null;
    if (exp === null || ended || !socket.connected) return;
    timer = setTimeout(() => {
      timer = null;
      if (socket.connected) void freshAccessToken(fetchImpl).then(() => keepFresh());
    }, Math.max(5_000, exp * 1000 - Date.now() - 25_000));
  };
  const unlisten = (() => {
    const l = (token: string) => {
      if (socket.connected) socket.emit(TABLE_TOKEN, { token });
      keepFresh();
    };
    refreshedListeners.add(l);
    return () => { refreshedListeners.delete(l); };
  })();
  let retried = false;
  socket.on('connect', () => { retried = false; keepFresh(); });
  socket.on('disconnect', (reason: string) => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (reason === 'io client disconnect') unlisten();
  });
  socket.on('connect_error', (err: Error) => {
    // Early access (temporary): the account has not entered the key; the page shows the screen.
    if (err?.message === 'locked') { tellLocked(); return; }
    if (socket.active || retried || ended || err?.message !== 'unauthorized') return;
    retried = true;
    void refreshOnce(fetchImpl).then((outcome) => { if (outcome === 'ok') socket.connect(); });
  });
  return socket;
}
